import {
  unfiledFolderToken,
  type DatasetFolderNode,
  type DatasetFolderTree,
} from "@/api/datasetFolder";

// 폴더 축의 순수 로직. 선택 상태, URL 토큰 왕복, 트리 평탄화, 건수 합을 다룬다.
// React 결합부(훅·컴포넌트)는 이 파일을 사용만 하고, React 코드는 여기에 두지 않는다
// (`utils/pageParam.ts` 와 같은 방식).

// 트리에서 고른 대상. **단일 선택**이다. BE 는 콤마 다중값을 받는다. 그러나 트리 노드의
// `dataset_count` 와 목록 `total` 이 1:1 로 맞아야 트리가 "왜 빠졌나" 를 설명하는 표가 된다.
// 여럿을 OR 로 합치면 그 등식이 깨진다.
export type FolderSelection =
  | { kind: "all" }
  | { kind: "unfiled" }
  | { kind: "folder"; uuid: string };

export const allFolderSelection: FolderSelection = { kind: "all" };

// 트리의 고정 행 둘. 접힌 레인의 칩과 펼친 레인의 행이 같은 이름을 써야 하므로 여기 둔다.
export const allFolderLabel = "전체";
export const unfiledFolderLabel = "미분류";

export const folderSearchParamKey = "folder";

// uuid 형식만 확인한다. 존재 여부는 서버가 판정한다(없는 uuid 는 0건). FE 는 형식 위반만
// 전체 선택으로 바꾼다. URL 은 외부 입력이라 어떤 문자열이든 들어올 수 있다.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * URL `?folder=` 토큰을 선택 상태로 읽는다. 알 수 없는 값은 전체 선택으로 바꾼다.
 *
 * 값은 폴더 uuid 다. 이름은 부모가 다르면 중복될 수 있으므로 키가 될 수 없다.
 */
export function folderSelectionFromParam(raw: string | null): FolderSelection {
  if (raw === null || raw === "") {
    return allFolderSelection;
  }
  if (raw === unfiledFolderToken) {
    return { kind: "unfiled" };
  }
  return UUID_PATTERN.test(raw) ? { kind: "folder", uuid: raw } : allFolderSelection;
}

/**
 * 선택 상태의 wire 토큰. 전체면 null 이고, 그때 `?folder=` 키와 요청 파라미터 둘 다 빠진다.
 *
 * URL 토큰과 API 질의값이 같은 어휘이므로 함수를 하나만 둔다. 둘로 나누면 한쪽만 고쳐질 수
 * 있고, 그러면 링크가 여는 목록과 화면이 어긋난다.
 */
export function folderToken(selection: FolderSelection): string | null {
  switch (selection.kind) {
    case "all":
      return null;
    case "unfiled":
      return unfiledFolderToken;
    case "folder":
      return selection.uuid;
  }
}

/**
 * `?folder=` 를 query string 에 기록한다. 전체면 키를 빼 URL 을 깨끗이 유지한다.
 *
 * **폴더가 바뀌면 `page` 를 함께 지운다.** 좁힌 뒤에도 이전 페이지 번호가 남으면, 결과가
 * 그보다 짧을 때 빈 화면이 뜬다(탭 전환이 쓰는 cascade 와 같은 규칙).
 *
 * @param params 현재 query string. 변형하지 않는다.
 * @param selection 새 선택.
 * @param pageKey 함께 지울 page 키.
 * @returns 새 query string.
 */
export function writeFolderParam(
  params: URLSearchParams,
  selection: FolderSelection,
  pageKey = "page",
): URLSearchParams {
  const next = new URLSearchParams(params);
  const token = folderToken(selection);
  if (token === null) {
    next.delete(folderSearchParamKey);
  } else {
    next.set(folderSearchParamKey, token);
  }
  next.delete(pageKey);
  return next;
}

export function isSameFolderSelection(left: FolderSelection, right: FolderSelection): boolean {
  return folderToken(left) === folderToken(right);
}

// 드롭다운 한 줄. 트리를 평탄화했을 때의 노드 하나다.
export type FlatFolder = {
  uuid: string;
  // 표시 경로. 루트는 `Picking`, 자식은 `Picking / 2026-Q1` 이다. 트리가 2계층이므로 더 깊어지지
  // 않는다. c6 드롭다운 옵션에는 group·depth 필드가 없어 계층을 라벨로 표현한다.
  path: string;
  displayName: string;
  depth: number;
  // 자손을 포함한 건수(트리 응답 그대로).
  datasetCount: number;
};

/**
 * 트리를 깊이 우선 순서로 평탄화한다. 부모 바로 뒤에 그 자식들이 온다.
 *
 * 형제 순서는 서버가 정한 이름순을 그대로 쓴다(FE 가 다시 정렬하지 않는다).
 *
 * @param tree 폴더 트리 응답.
 * @returns 평탄화된 폴더 목록.
 */
export function flattenFolderTree(tree: DatasetFolderTree): readonly FlatFolder[] {
  const flat: FlatFolder[] = [];
  const walk = (nodes: readonly DatasetFolderNode[], depth: number, prefix: string): void => {
    for (const node of nodes) {
      const path = prefix === "" ? node.display_name : `${prefix} / ${node.display_name}`;
      flat.push({
        uuid: node.uuid,
        path,
        displayName: node.display_name,
        depth,
        datasetCount: node.dataset_count,
      });
      walk(node.children, depth + 1, path);
    }
  };
  walk(tree.folders, 0, "");
  return flat;
}

/**
 * 트리 전체의 데이터셋 수. `전체 데이터셋` 행이 표시하는 값이다.
 *
 * **루트 노드들의 합 + 미분류**다. 각 노드의 `dataset_count` 가 이미 자손을 포함하므로
 * 자식을 다시 더하면 중복된다. 이 합은 목록 기본 스코프의 `total` 과 같아야 한다.
 *
 * @param tree 폴더 트리 응답.
 * @returns 전체 건수.
 */
export function totalDatasetCount(tree: DatasetFolderTree): number {
  const roots = tree.folders.reduce((sum, node) => sum + node.dataset_count, 0);
  return roots + tree.unfiled_count;
}

// 2계층 트리에서 자식이 있는 루트만 펼칠 수 있다. 빈 폴더는 첫 자식이 생겼을 때 펼친다.
export function expandableFolderUuids(tree: DatasetFolderTree): ReadonlySet<string> {
  return new Set(tree.folders.filter((node) => node.children.length > 0).map((node) => node.uuid));
}

/**
 * 어떤 폴더가 이 선택의 조상인가. 선택된 자식이 있는 부모는 펼쳐 둔다.
 *
 * @param tree 폴더 트리 응답.
 * @param selection 현재 선택.
 * @returns 펼쳐 둘 부모 uuid 묶음.
 */
export function ancestorFolderUuids(
  tree: DatasetFolderTree,
  selection: FolderSelection,
): ReadonlySet<string> {
  if (selection.kind !== "folder") {
    return new Set();
  }
  for (const root of tree.folders) {
    if (root.children.some((child) => child.uuid === selection.uuid)) {
      return new Set([root.uuid]);
    }
  }
  return new Set();
}

// 선택이 가리키는 대상의 이름.
export type FolderSelectionLabel = {
  // 행·칩에 그리는 짧은 이름.
  name: string;
  // 툴팁이 쓰는 전체 경로. 고정 행과 루트 폴더는 name 과 같다.
  path: string;
};

/**
 * 선택을 표시 이름으로 옮긴다.
 *
 * 고정 행 둘은 트리 없이도 이름이 정해진다. 그래서 `tree` 가 null 이어도 값을 돌려준다. 트리
 * 로드가 실패해도 "지금 전체를 보고 있다" 는 표시할 수 있어야 한다.
 *
 * @param tree 폴더 트리. 아직 못 받았으면 null.
 * @param selection 현재 선택.
 * @returns 이름과 경로. 트리에 없는 uuid(지워진 폴더를 가리키는 링크)면 null 이다.
 *   이름을 만들어 내지 않는다.
 */
export function folderSelectionLabel(
  tree: DatasetFolderTree | null,
  selection: FolderSelection,
): FolderSelectionLabel | null {
  if (selection.kind === "all") {
    return { name: allFolderLabel, path: allFolderLabel };
  }
  if (selection.kind === "unfiled") {
    return { name: unfiledFolderLabel, path: unfiledFolderLabel };
  }
  if (tree === null) {
    return null;
  }
  const flat = flattenFolderTree(tree).find((folder) => folder.uuid === selection.uuid);
  return flat === undefined ? null : { name: flat.displayName, path: flat.path };
}

/**
 * 접힌 레인의 칩이 쓰는 한 글자 표식.
 *
 * 정사각 한 칸에는 한 글자가 맞다. 두 글자 두문자는 사용자가 쓴 적 없는 약어를 만든다
 * (`Picking Q1` → `PQ`). 그래서 사람 이름용 `formatMemoAuthorInitials` 를 쓰지 않는다.
 *
 * 코드 포인트 단위로 자른다. `charAt(0)` 은 이모지로 시작하는 이름의 서로게이트 페어를
 * 반으로 잘라 깨진 글리프를 남긴다.
 *
 * 같은 모양의 함수가 `SettingsModal` 에 하나 더 있다. 세 번째가 생기면 `utils` 로 올린다.
 *
 * @param name 폴더 표시 이름.
 * @returns 대문자 한 글자. 이름이 비면 `?`.
 */
export function folderMonogram(name: string): string {
  return [...name.trim()][0]?.toUpperCase() ?? "?";
}
